import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Rating,
  Tooltip
} from '@mui/material';
import { createCustomer } from '../../firebase/services';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useLanguage } from '../../context/LanguageContext';

interface CustomerCreationFormProps {
  onCustomerCreated: (customerId: string, customerData: any) => void;
  onError: (message: string) => void;
}

interface Address {
  id: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  isMain: boolean;
}

// Fonction pour générer un ID client unique
const generateCustomerId = (): string => {
  const timestamp = new Date().getTime().toString().slice(-6);
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `CLI-${timestamp}-${random}`;
};

const CustomerCreationForm: React.FC<CustomerCreationFormProps> = ({ 
  onCustomerCreated, 
  onError 
}) => {
  const { t } = useLanguage();
  
  // États pour le formulaire
  const [companyName, setCompanyName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [rating, setRating] = useState<number | null>(0);
  const [notes, setNotes] = useState('');
  const [currentAddress, setCurrentAddress] = useState<Address>({
    id: '',
    street: '',
    city: '',
    postalCode: '',
    country: '',
    isMain: false
  });
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Générer un ID client au chargement du composant
  useEffect(() => {
    setCustomerId(generateCustomerId());
  }, []);

  // Générer un nouvel ID client
  const handleGenerateNewId = () => {
    setCustomerId(generateCustomerId());
  };

  // Copier l'ID client dans le presse-papiers
  const handleCopyId = () => {
    navigator.clipboard.writeText(customerId);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };
  
  // Validation des formulaires
  const [errors, setErrors] = useState<{
    companyName?: string;
    email?: string;
    logoUrl?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  }>({});

  // Validation du formulaire
  const validateForm = () => {
    const newErrors: {
      companyName?: string;
      email?: string;
      logoUrl?: string;
    } = {};
    
    if (!companyName) {
      newErrors.companyName = t('admin.customerForm.error.companyName.required') as string;
    }
    
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('admin.customerForm.error.email.invalid') as string;
    }
    
    if (logoUrl && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/.test(logoUrl)) {
      newErrors.logoUrl = t('admin.customerForm.error.logoUrl.invalid') as string;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validation de l'adresse
  const validateAddress = () => {
    const newErrors: {
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
    } = {};
    
    if (!currentAddress.street) {
      newErrors.street = t('admin.customerForm.error.street.required') as string;
    }
    
    if (!currentAddress.city) {
      newErrors.city = t('admin.customerForm.error.city.required') as string;
    }
    
    if (!currentAddress.postalCode) {
      newErrors.postalCode = t('admin.customerForm.error.postalCode.required') as string;
    }
    
    if (!currentAddress.country) {
      newErrors.country = t('admin.customerForm.error.country.required') as string;
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Ajouter ou mettre à jour une adresse
  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateAddress()) {
      return;
    }
    
    if (editingAddressId) {
      // Mise à jour d'une adresse existante
      setAddresses(addresses.map(addr => 
        addr.id === editingAddressId ? { ...currentAddress, id: editingAddressId } : addr
      ));
      setEditingAddressId(null);
    } else {
      // Ajout d'une nouvelle adresse
      const newAddress = {
        ...currentAddress,
        id: Date.now().toString(),
        isMain: addresses.length === 0 ? true : currentAddress.isMain
      };
      
      // Si la nouvelle adresse est définie comme principale, mettre à jour les autres
      if (newAddress.isMain) {
        setAddresses(addresses.map(addr => ({ ...addr, isMain: false })).concat(newAddress));
      } else {
        setAddresses([...addresses, newAddress]);
      }
    }
    
    // Réinitialiser le formulaire d'adresse
    setCurrentAddress({
      id: '',
      street: '',
      city: '',
      postalCode: '',
      country: '',
      isMain: false
    });
    setShowAddressForm(false);
  };

  // Éditer une adresse
  const handleEditAddress = (address: Address) => {
    setCurrentAddress(address);
    setEditingAddressId(address.id);
    setShowAddressForm(true);
  };

  // Supprimer une adresse
  const handleDeleteAddress = (id: string) => {
    const addressToDelete = addresses.find(addr => addr.id === id);
    let updatedAddresses = addresses.filter(addr => addr.id !== id);
    
    // Si l'adresse supprimée était l'adresse principale et qu'il reste des adresses,
    // définir la première adresse restante comme principale
    if (addressToDelete?.isMain && updatedAddresses.length > 0) {
      updatedAddresses = [
        { ...updatedAddresses[0], isMain: true },
        ...updatedAddresses.slice(1)
      ];
    }
    
    setAddresses(updatedAddresses);
  };

  // Définir une adresse comme principale
  const handleSetMainAddress = (id: string) => {
    setAddresses(
      addresses.map(addr => ({
        ...addr,
        isMain: addr.id === id
      }))
    );
  };

  // Créer un nouveau client
  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setCreating(true);
      
      const customerData = {
        customerId,
        name: companyName,
        companyName,
        logoUrl,
        addresses,
        rating,
        notes,
        phone,
        email,
        website,
        description,
        status: 'active' as const
      };
      
      const newCustomer = await createCustomer(customerData);
      
      // Réinitialiser le formulaire
      setCompanyName('');
      setCustomerId(generateCustomerId());
      setLogoUrl('');
      setAddresses([]);
      setRating(0);
      setNotes('');
      setPhone('');
      setEmail('');
      setWebsite('');
      setDescription('');
      
      // Notifier le composant parent
      onCustomerCreated(newCustomer.id, customerData);
      
    } catch (error) {
      console.error("Error creating customer:", error);
      onError(t('admin.customerForm.error.generic') as string);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <BusinessIcon />
        {t('admin.customerForm.title')}
      </Typography>
      
      <form onSubmit={handleCreateCustomer}>
        <Grid container spacing={2}>
          {/* ID Client */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2" sx={{ minWidth: '80px' }}>
                {t('admin.customerForm.clientId')}:
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  flex: 1
                }}
              >
                {customerId}
              </Typography>
              <Tooltip title={copySuccess ? t('admin.customerForm.copied') as string : t('admin.customerForm.copy') as string}>
                <IconButton onClick={handleCopyId} size="small">
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('admin.customerForm.regenerate') as string}>
                <IconButton onClick={handleGenerateNewId} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              label={t('admin.customerForm.companyName') as string}
              fullWidth
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('admin.customerForm.logoUrl') as string}
              fullWidth
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              error={!!errors.logoUrl}
              helperText={errors.logoUrl || t('admin.customerForm.logoUrlHint') as string}
              InputProps={{
                startAdornment: <ImageIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />
          </Grid>

          {/* Système de notation */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'background.paper', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                {t('admin.customerForm.rating') as string}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating
                  name="customer-rating"
                  value={rating}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                  }}
                  precision={0.5}
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderIcon fontSize="inherit" />}
                />
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  {rating ? `${rating}/5` : t('admin.customerForm.notRated') as string}
                </Typography>
              </Box>
              <TextField
                label={t('admin.customerForm.notes') as string}
                placeholder={t('admin.customerForm.notesPlaceholder') as string}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                {t('admin.customerForm.addresses') as string}
              </Typography>
              
              {addresses.length > 0 ? (
                <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  {addresses.map((address) => (
                    <React.Fragment key={address.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" fontWeight={address.isMain ? 600 : 400}>
                                {address.street}, {address.postalCode} {address.city}, {address.country}
                              </Typography>
                              {address.isMain && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    ml: 1, 
                                    bgcolor: 'primary.main', 
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1
                                  }}
                                >
                                  {t('admin.customerForm.main') as string}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          {!address.isMain && (
                            <Button 
                              size="small" 
                              onClick={() => handleSetMainAddress(address.id)}
                              sx={{ mr: 1 }}
                            >
                              {t('admin.customerForm.setAsMain') as string}
                            </Button>
                          )}
                          <IconButton 
                            edge="end" 
                            aria-label="edit" 
                            onClick={() => handleEditAddress(address)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            aria-label="delete" 
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('admin.customerForm.noAddresses') as string}
                </Typography>
              )}
              
              {!showAddressForm ? (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddressForm(true)}
                  sx={{ mt: 2 }}
                >
                  {t('admin.customerForm.addAddress') as string}
                </Button>
              ) : (
                <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    {editingAddressId ? t('admin.customerForm.editAddress') as string : t('admin.customerForm.newAddress') as string}
                  </Typography>
                  
                  <form onSubmit={handleAddressSubmit}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label={t('admin.customerForm.street') as string}
                          fullWidth
                          value={currentAddress.street}
                          onChange={(e) => setCurrentAddress({ ...currentAddress, street: e.target.value })}
                          error={!!errors.street}
                          helperText={errors.street}
                          required
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label={t('admin.customerForm.city') as string}
                          fullWidth
                          value={currentAddress.city}
                          onChange={(e) => setCurrentAddress({ ...currentAddress, city: e.target.value })}
                          error={!!errors.city}
                          helperText={errors.city}
                          required
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label={t('admin.customerForm.postalCode') as string}
                          fullWidth
                          value={currentAddress.postalCode}
                          onChange={(e) => setCurrentAddress({ ...currentAddress, postalCode: e.target.value })}
                          error={!!errors.postalCode}
                          helperText={errors.postalCode}
                          required
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label={t('admin.customerForm.country') as string}
                          fullWidth
                          value={currentAddress.country}
                          onChange={(e) => setCurrentAddress({ ...currentAddress, country: e.target.value })}
                          error={!!errors.country}
                          helperText={errors.country}
                          required
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                          <Button 
                            type="button" 
                            onClick={() => {
                              setShowAddressForm(false);
                              setEditingAddressId(null);
                              setCurrentAddress({
                                id: '',
                                street: '',
                                city: '',
                                postalCode: '',
                                country: '',
                                isMain: false
                              });
                            }}
                          >
                            {t('admin.customerForm.cancel') as string}
                          </Button>
                          <Button 
                            type="submit" 
                            variant="contained"
                          >
                            {editingAddressId ? t('admin.customerForm.update') as string : t('admin.customerForm.add') as string}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              )}
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.customerForm.phone') as string}
              fullWidth
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.customerForm.email') as string}
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.customerForm.website') as string}
              fullWidth
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('admin.customerForm.description') as string}
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={creating}
              startIcon={creating ? <CircularProgress size={20} /> : <BusinessIcon />}
            >
              {creating ? t('admin.customerForm.creating') as string : t('admin.customerForm.create') as string}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CustomerCreationForm; 